import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'store';
import { setFocusedEditMode, setFullscreenMode } from 'store/reducers/menu';

const FULLSCREEN_PREFS_KEY = 'fullscreen-mode-preferences';

export const getFullscreenPreferences = (): Record<string, boolean> => {
    try {
        const saved = localStorage.getItem(FULLSCREEN_PREFS_KEY);
        return saved ? JSON.parse(saved) : {};
    } catch {
        return {};
    }
};

const saveFullscreenPreference = (pageKey: string, value: boolean) => {
    try {
        const prefs = getFullscreenPreferences();
        prefs[pageKey] = value;
        localStorage.setItem(FULLSCREEN_PREFS_KEY, JSON.stringify(prefs));
    } catch {
        // ignore storage errors
    }
};

/**
 * Activates focused edit mode on mount: hides the main sidebar navigation
 * and header, shows a grey background for a distraction-free form experience.
 * Automatically deactivates on unmount.
 *
 * The route that renders the form names the same key as its `focusedEdit` (`routes/MainRoutes`), so
 * the layout already lays the page out focused before this runs. Under a route without it, the layout
 * keeps its chrome. A form in the create dialog is on no route of its own and leaves the layout as it is.
 *
 * @param pageKey - Optional key to persist fullscreen preference per page
 *                  (e.g., 'contacts', 'tasks'). Each page remembers its own
 *                  fullscreen/sidebar choice independently.
 */
const useFocusedEditMode = (pageKey?: string) => {
    const dispatch = useDispatch();
    const { fullscreenMode } = useSelector((state) => state.menu);
    const isRestoringRef = useRef(false);
    const inCreateDialog = useInCreateDialog();

    // TODO(react-doctor: no-cascading-set-state): mount/unmount lifecycle —
    // dispatches focused-edit-mode plus optional persisted fullscreen preference.
    // The two redux fields are independent UI state.
    useEffect(() => {
        if (inCreateDialog) return;
        dispatch(setFocusedEditMode(true));

        if (pageKey) {
            const prefs = getFullscreenPreferences();
            const saved = prefs[pageKey];
            if (saved !== undefined) {
                isRestoringRef.current = true;
                dispatch(setFullscreenMode(saved));
            }
        }

        return () => {
            dispatch(setFocusedEditMode(false));
        };
    }, [dispatch, pageKey, inCreateDialog]);

    // Persist fullscreen preference when user toggles it (skip the initial restore)
    useEffect(() => {
        if (!pageKey || inCreateDialog) return;
        if (isRestoringRef.current) {
            isRestoringRef.current = false;
            return;
        }
        saveFullscreenPreference(pageKey, fullscreenMode);
    }, [pageKey, fullscreenMode, inCreateDialog]);
};

export default useFocusedEditMode;
